import { isNil } from 'lodash';
import moment from 'moment';
import { MESSAGE_GROUPING_TIME_SPAN } from '@shared/styles';
import { notNil } from '@shared/util/notNil';

const groupableSystemMessageSubTypes: MBAPI.MessageSubType[] = [
  'CARD_CREATED',
  'CARD_DELETED',
  'CARD_UPDATED',
  // 'CARD_REMINDER_SENT' as any
];

export interface MBReadMarker {
  readAt: string;
  user: MBAPI.ShortUser;
}

/*
    Given a list of messages, we calculate the layout options and update the store.
    This is what makes messages grouped, shows date markers, read markers, seen by etc.
    Note: we want to dispatch as few updates as possible. The reason is that messages
    are re-rendered when the object is updated (aka shallow diff is false), so updating
    the store with an object that has the exact same props doesn't change the UI but
    makes React re-render all messages, which is wasteful.

    To understand how the MBMessage component works, look in scenes/chat/containers/message.tsx

    Here is some rough logic outlined (needs updating):
    * If the message is a TEXT message and has been sent:
        * If a message is the first of the day, add a date marker (isFirstUnread)
        * Else:
            * If the message above is is sent by the same user (isGroupedWithAbove):
                1. prevent top corner radius
            * If the message below is sent by the same user (isGroupedWithBelow):
                1. prevent bottom corner radius
                2. show no profile picture

      If the message is the last sent message the user has (isUsersLastSentMessage):
        * Show delivered status

      If the message is the last message of all (isLastMessage)
        * Show seen by as pictures instead of as a number

    TODO(fant): how does seen by work?
    TODO(fant): when do we show delivery status sent?
    TODO(fant): this needs a ton of documentation and tests...
}
*/

export function calculateMessageLayoutOptions(
  messages: MBAPI.ChatMessage[],
  readMarkers: MBAPI.ChannelChatThread['userReadMarkers'],
  myReadAt: moment.Moment | null,
): MBChatMessageLayoutOptions[] {
  // We want to show "Delivered ✅" below the last message. To be able to set
  // that flag (isUsersLastSentMessage), we get its index here and later
  // compare the indices of the messages we loop over. We look for:
  // 1. a message sent by the user
  // 2. that is an actual text message (eg not a system message caused by the user)
  const indexOfUsersLastSentMessage = messages.findIndex(
    message => message.type === 'TEXT' && message.sender.isMe,
  );

  return messages.map((message: MBAPI.ChatMessage, index: number) => {
    // get the message above and below in the chat thread to determine grouping
    const messageAbove: MBAPI.ChatMessage | null = messages[index + 1];
    const messageBelow: MBAPI.ChatMessage | null = messages[index - 1];

    const createdAt = moment(message.createdAt);
    const isSameDayAsAbove = !!messageAbove && createdAt.isSame(messageAbove.createdAt, 'day');
    const isSameDayAsBelow = !!messageBelow && createdAt.isSame(messageBelow.createdAt, 'day');

    const isWithinTimeAbove =
      !!messageAbove && createdAt.diff(messageAbove.createdAt) < MESSAGE_GROUPING_TIME_SPAN;
    const isWithinTimeBelow =
      !!messageBelow && -createdAt.diff(messageBelow.createdAt) < MESSAGE_GROUPING_TIME_SPAN;

    const isAboveFirstUnreadMessage =
      !!myReadAt &&
      !!messageBelow &&
      !myReadAt.isBefore(createdAt) &&
      myReadAt.isBefore(messageBelow.createdAt);
    const isBelowLastReadMessage =
      !!myReadAt &&
      !!messageAbove &&
      !myReadAt.isBefore(messageAbove.createdAt) &&
      myReadAt.isBefore(createdAt);

    // We want to show a date marker over the first message of each day.
    // Note(fant): here we could also insert more date markers (eg the first
    // message within the last X hours, similar to how some other apps do it)
    const isDateMarkerVisible = !!messageAbove && !isSameDayAsAbove;

    // If the message above/below (1) exists, (2) is text and (3) is sent by
    // the same user: group the messages. This currently only determines
    // if the other users' profile pictures will be shown or not. Later it
    // can modify things like message bubble border radius.
    const isGroupedWithAbove =
      !!messageAbove &&
      isSameDayAsAbove &&
      !isBelowLastReadMessage &&
      isWithinTimeAbove &&
      !messageAbove.urgent &&
      !message.urgent &&
      messageAbove.type === 'TEXT' &&
      message.type === 'TEXT' &&
      messageAbove.sender.id === message.sender.id;
    const isGroupedWithBelow =
      !!messageBelow &&
      isSameDayAsBelow &&
      !isAboveFirstUnreadMessage &&
      isWithinTimeBelow &&
      !messageBelow.urgent &&
      !message.urgent &&
      messageBelow.type === 'TEXT' &&
      message.type === 'TEXT' &&
      messageBelow.sender.id === message.sender.id;

    // Show read marker over the first unread message
    // Note: we don't show this for the absolutely first message
    const isReadMarkerVisible = isBelowLastReadMessage;

    // If this is the last message sent by the user (as calculated above)
    const isUsersLastSentMessage = index === indexOfUsersLastSentMessage;

    // If this is the last message in the list (show seen by differently)
    const isLastMessage = index === 0;

    // List of all users who have read the message
    const seenByUsers = (readMarkers ?? [])
      .filter(notNil)
      // We get all messages that are not after. Why not isBefore? Because
      // we want messages that are before or at the exact same time. This
      // is not really an issue in production but makes some Storybook
      // examples cleaner.
      .filter(({ readAt }) => !createdAt.isAfter(readAt))
      // Don't show myself as seen by
      .filter(({ user }) => !user.isMe)
      // Don't show the sending user as having read the message
      .filter(({ user }) => user.id !== message.sender.id)
      .map(({ user }: MBReadMarker) => user);

    const groupedSystemMessages = getGroupedSystemMessages(messages, index);
    const isGroupedSystemMessage =
      !!groupedSystemMessages?.length || shouldGroup(message, messageBelow);

    return {
      isDateMarkerVisible,
      isGroupedWithAbove,
      isGroupedWithBelow,
      isReadMarkerVisible,
      isUsersLastSentMessage,
      isLastMessage,
      seenByUsers,
      groupedSystemMessages,
      isGroupedSystemMessage,
    };
  });
}

function isGroupableSystemMessage(message: MBAPI.ChatMessage): boolean {
  return (
    message.type === 'SYSTEM' &&
    !isNil(message.subType) &&
    groupableSystemMessageSubTypes.includes(message.subType)
  );
}

function isSameDay(a: MBAPI.ChatMessage, b: MBAPI.ChatMessage): boolean {
  return moment(a.createdAt).isSame(b.createdAt, 'day');
}

function shouldGroup(a: MBAPI.ChatMessage, b: MBAPI.ChatMessage): boolean {
  return (
    !!a && !!b && isSameDay(a, b) && isGroupableSystemMessage(a) && isGroupableSystemMessage(b)
  );
}

function getGroupedSystemMessages(
  messages: MBAPI.ChatMessage[],
  index: number,
): MBAPI.ChatMessage[] | null {
  const firstMessageInGroup = messages[index];
  const messageBelow = messages[index - 1];
  if (shouldGroup(firstMessageInGroup, messageBelow)) {
    return null;
  }

  let groupedSystemMessages: MBAPI.ChatMessage[] = [];
  for (let i = index; i < messages.length; i++) {
    const message = messages[i];
    if (!shouldGroup(message, firstMessageInGroup)) {
      break;
    }
    groupedSystemMessages.push(message);
  }

  if (groupedSystemMessages.length <= 1) {
    return null;
  }
  return groupedSystemMessages;
}
